"use client";

import { useState, useCallback } from "react";
import { ChevronRight, ChevronDown } from "lucide-react";
import { ResourceFormRenderer, mcpSchemaToFormSchema, cleanFormValues } from "@/components/resource-form";
import type { ResourceSchema } from "@/components/resource-form";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { isResourceListMessage, ResourceListMessage } from "./resource-list-message";

/**
 * Resource form message payload from agent
 */
export interface ResourceFormPayload {
  type: "resource_form";
  action: "create" | "edit" | "view";
  resource_type: string;
  resource_id?: string; // For edit/view
  schema: unknown; // MCP schema response
  values?: Record<string, unknown>; // Pre-filled values for edit
  message?: string; // Optional instruction text
}

/**
 * Extract JSON object from content that may have surrounding text
 * Handles: pure JSON, markdown code blocks, or JSON embedded in text
 */
function extractJson(content: string): string | null {
  const trimmed = content.trim();

  // Case 1: Pure JSON
  if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
    return trimmed;
  }

  // Case 2: Markdown code block ```json ... ``` or ``` ... ```
  const codeBlockMatch = trimmed.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/);
  if (codeBlockMatch) {
    return codeBlockMatch[1];
  }

  // Case 3: JSON embedded in text - find the JSON object
  // Look for {"type":"resource_form" or {"type":"resource_list"
  const jsonMatch = trimmed.match(/(\{"type"\s*:\s*"resource_(?:form|list)"[\s\S]*\})/);
  if (jsonMatch) {
    // Try to extract valid JSON by finding matching braces
    const start = trimmed.indexOf(jsonMatch[1]);
    if (start >= 0) {
      let depth = 0;
      let end = start;
      for (let i = start; i < trimmed.length; i++) {
        if (trimmed[i] === '{') depth++;
        else if (trimmed[i] === '}') {
          depth--;
          if (depth === 0) {
            end = i + 1;
            break;
          }
        }
      }
      if (depth === 0) {
        return trimmed.slice(start, end);
      }
    }
  }

  return null;
}

/**
 * Check if content is a resource form message
 */
export function isResourceFormMessage(content: string): ResourceFormPayload | null {
  try {
    const jsonStr = extractJson(content);
    if (!jsonStr) return null;

    const parsed = JSON.parse(jsonStr);

    // Check if it has the resource_form type
    if (parsed.type === "resource_form" && parsed.schema) {
      return parsed as ResourceFormPayload;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Props for ResourceFormMessage
 */
interface ResourceFormMessageProps {
  payload: ResourceFormPayload;
  onSubmit: (response: string) => void;
}

/**
 * ResourceFormMessage - Renders a dynamic form based on agent response
 */
export function ResourceFormMessage({ payload, onSubmit }: ResourceFormMessageProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  // Convert MCP schema to form schema
  const formSchema: ResourceSchema = mcpSchemaToFormSchema(payload.schema as never);

  // Handle form submission
  const handleSubmit = useCallback(
    async (values: Record<string, unknown>) => {
      setIsSubmitting(true);
      setResult(null);

      try {
        // Clean up empty values
        const cleanedValues = cleanFormValues(values);

        // Send values back to agent as JSON response
        const response = JSON.stringify({
          action: payload.action,
          resource_type: payload.resource_type,
          resource_id: payload.resource_id,
          values: cleanedValues,
        });

        onSubmit(response);

        setResult({
          success: true,
          message: payload.action === "create"
            ? "Creating resource..."
            : "Updating resource...",
        });
      } catch (err) {
        setResult({
          success: false,
          message: err instanceof Error ? err.message : "Failed to submit form",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [payload, onSubmit]
  );

  // Handle validation (dry-run)
  const handleValidate = useCallback(
    async (values: Record<string, unknown>) => {
      setIsValidating(true);

      try {
        const cleanedValues = cleanFormValues(values);

        // Send validation request back to agent
        const response = JSON.stringify({
          action: "validate",
          resource_type: payload.resource_type,
          values: cleanedValues,
        });

        onSubmit(response);

        // The actual validation result will come in a subsequent message
        return { valid: true };
      } catch (err) {
        return {
          valid: false,
          errors: { _form: err instanceof Error ? err.message : "Validation failed" },
        };
      } finally {
        setIsValidating(false);
      }
    },
    [payload, onSubmit]
  );

  // Handle cancel
  const handleCancel = useCallback(() => {
    onSubmit(JSON.stringify({ action: "cancel" }));
  }, [onSubmit]);

  // Debug logging
  console.log('📝 ResourceFormMessage payload:', { action: payload.action, values: payload.values, schema: payload.schema });

  return (
    <div className="my-2 rounded border border-border bg-card p-3 text-sm">
      {/* Optional instruction message */}
      {payload.message && (
        <div className="mb-3 text-xs text-foreground">{payload.message}</div>
      )}

      {/* Form */}
      <ResourceFormRenderer
        schema={formSchema}
        initialValues={payload.values || {}}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        mode={payload.action === "view" ? "view" : payload.action}
        isLoading={isSubmitting}
      />

      {/* Result message */}
      {result && (
        <div
          className={`mt-4 flex items-center gap-2 rounded p-3 ${
            result.success
              ? "bg-emerald-900/20 text-emerald-300"
              : "bg-red-900/20 text-red-300"
          }`}
        >
          {result.success ? (
            isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )
          ) : (
            <XCircle className="h-4 w-4" />
          )}
          <span className="text-sm">{result.message}</span>
        </div>
      )}
    </div>
  );
}

/**
 * ResourceFormWrapper - Used in message rendering to detect and render forms/lists
 */
interface ResourceFormWrapperProps {
  text: string;
  onSubmitResponse?: (response: string) => void;
  onViewResource?: (resourceId: string) => void;
  onEditResource?: (resourceId: string) => void;
  onDeleteResource?: (resourceId: string) => void;
}

/**
 * Expandable text content - shows truncated with "Show more" link
 */
function ExpandableText({
  text,
  previewLength = 400,
}: {
  text: string;
  previewLength?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const needsTruncation = text.length > previewLength;

  if (!needsTruncation) {
    return (
      <div className="text-foreground whitespace-pre-wrap">
        {text}
      </div>
    );
  }

  return (
    <div className="text-foreground">
      <div className="whitespace-pre-wrap">
        {expanded ? text : text.slice(0, previewLength) + "..."}
      </div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="mt-1 text-xs text-emerald-400 hover:text-emerald-300 transition-colors flex items-center gap-1"
      >
        {expanded ? (
          <>
            <ChevronDown className="w-3 h-3 rotate-180" />
            Show less
          </>
        ) : (
          <>
            <ChevronRight className="w-3 h-3" />
            Show more ({Math.round(text.length / 1000)}k chars)
          </>
        )}
      </button>
    </div>
  );
}

export function ResourceFormWrapper({
  text,
  onSubmitResponse,
  onViewResource,
  onEditResource,
  onDeleteResource,
}: ResourceFormWrapperProps) {
  // Check for resource list first
  const listPayload = isResourceListMessage(text);
  if (listPayload) {
    return (
      <ResourceListMessage
        payload={listPayload}
        onView={onViewResource}
        onEdit={onEditResource}
        onDelete={onDeleteResource}
      />
    );
  }

  // Check for resource form
  const formPayload = isResourceFormMessage(text);
  if (formPayload && onSubmitResponse) {
    return <ResourceFormMessage payload={formPayload} onSubmit={onSubmitResponse} />;
  }

  // Not a structured message, render as expandable text
  return <ExpandableText text={text} previewLength={400} />;
}
